<template>
    <div>
        <div class="filters-wrapper d-flex justify-content-start flex-wrap mb-3 mt-5">
            <div v-if="is_dashboard" class="single-filter search-filter-dropdown">
                <drop-down-filter
                    :key="'hotel-filter-' + filterStateVersion"
                    :id="'hotel-filter'"
                    :label="$t('hotel')"
                    :list-value-field="'name'"
                    :list="hotelList"
                    :active="formData.hotel_id"
                    @get-value="onHotelChange"
                />
            </div>
            <div v-if="is_dashboard" class="single-filter search-filter-dropdown">
                <drop-down-filter
                    :key="'staff-filter-' + filterStateVersion"
                    :id="'staff-filter'"
                    :label="$t('staff')"
                    :list-value-field="'employee_name'"
                    :list="staffList"
                    :active="formData.maid_id"
                    @get-value="onStaffChange"
                />
            </div>
            <div v-if="is_dashboard" class="single-filter">
                <multi-select-filter
                    :key="'employee-tags-filter-' + filterStateVersion"
                    :label="$t('employee_tags')"
                    :list-value-field="'name'"
                    :list="employeeTagList"
                    :active="formData.employee_tags"
                    @get-value="onEmployeeTagsChange"
                />
            </div>
            <!-- Date moved next to Hotel and Staff for dashboard layout -->
            <div v-if="is_dashboard" class="single-filter search-filter-dropdown">
                <date-range-picker
                    :key="'hotel-dashboard-date-' + filterStateVersion"
                    class="mb-0"
                    :preset-ranges="['today', 'thisMonth', 'last7Days', 'lastMonth']"
                    :init-range="dateRangePickerValue"
                    @changed="onDateRangeChange"
                />
            </div>

            <!-- Wrap inputs in a flex container aligned to bottom baseline (non-dashboard specific inputs) -->
            <div class="d-flex align-items-end w-100 flex-wrap" style="gap: 15px;">
                <div v-if="!is_dashboard" class="single-filter mb-0">
                    <app-form-group
                        class="mb-0"
                        type="password"
                        :label="$t('passcode')"
                        :placeholder="$t('passcode')"
                        v-model="formData.passcode"
                        :show-password="true"
                    />
                </div>
            </div>
        </div>

        <h2 v-if="hotelName" class="text-center mb-3">{{ hotelName }}</h2>

        <ul class="nav nav-tabs">
            <li v-for="tabItem in visibleTabs" :key="tabItem.id" class="nav-item">
                <a href="#" class="nav-link" :class="{'active': tab === tabItem.id}" @click.prevent="setTab(tabItem.id)">
                    {{ $t(tabItem.label) }}
                </a>
            </li>
            <!-- <li class="nav-item">
                <a href="#" class="nav-link" :class="{'active': tab === 'floor_assignment'}" @click.prevent="tab = 'floor_assignment'">
                    {{ $t('floor_assignment') }}
                </a>
            </li> -->
        </ul>

        <room-housekeeping-summary-tab
            v-if="tab === 'summary'"
            :hotel-id="formData.hotel_id"
            :maid-id="formData.maid_id"
            :employee-tags="formData.employee_tags"
            :from-date="formData.from_date"
            :to-date="formData.to_date"
        />

        <room-housekeeping-detail-tab
            v-if="tab === 'detail'"
            :hotel-id="formData.hotel_id"
            :maid-id="formData.maid_id"
            :employee-tags="formData.employee_tags"
            :from-date="formData.from_date"
            :to-date="formData.to_date"
            :passcode="formData.passcode"
        />

        <div v-if="tab === 'floor_assignment'" class="card border-0 bg-transparent">
            <div class="card-body p-0">
                <div class="datatable">
                    <app-overlay-loader v-if="floorAssignmentLoading"/>
                    <div v-if="floorAssignmentData && floorAssignmentData.length > 0"
                         class="table-responsive custom-scrollbar table-view-responsive pt-3 mt-3">
                         <table class="table mb-0">
                            <thead>
                            <tr>
                                <th class="datatable-th pt-2">
                                    <span class="font-size-default"><span>{{ $t('work_date') }}</span></span>
                                </th>
                                <th class="datatable-th pt-2">
                                    <span class="font-size-default"><span>{{ $t('floor') }}</span></span>
                                </th>
                                <th class="datatable-th pt-2">
                                    <span class="font-size-default"><span>{{ $t('employees') }}</span></span>
                                </th>
                                <th class="datatable-th pt-2">
                                    <span class="font-size-default"><span>{{ $t('remark') }}</span></span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(row, index) in floorAssignmentData" :key="index">
                                <td class="datatable-td">{{ formatDateToLocal(row.work_date) }}</td>
                                <td class="datatable-td">{{ row.floor }}</td>
                                <td class="datatable-td">
                                    <app-avatars-group
                                        :avatars-group-class="'avatars-group-w-50'"
                                        :name="'hotelShareAvatarsGroup' + index"
                                        :avatars="getAvatars(row.employees)"
                                        :border="true"
                                        :shadow="true"
                                    />
                                </td>
                                <td class="datatable-td">{{ row.remark }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else style="min-height: 400px;" class="position-relative"
                         :class="{'loading-opacity': floorAssignmentLoading}">
                        <app-empty-data-block :message="$t('empty_data_block_dummy_message')"/>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasCleaningTab && tab === 'cleaning'" class="card border-0 bg-transparent">
            <div v-if="typeLoading" class="card-body">
                <app-overlay-loader v-if="typeLoading"/>
            </div>
            <div v-if="!typeLoading" class="row pt-primary">
                <div class="col-12">
                    <app-chart
                        v-if="lineChart.labels.length > 0"
                        type="line-chart"
                        :height="380"
                        :maximum-range="20"
                        :labels="lineChart.labels"
                        :data-sets="lineChart.dataSet"
                    />
                </div>
                <div class="col-12 mt-5">
                    <app-chart
                        v-if="doughChart.labels.length > 0"
                        class="mb-primary"
                        type="dough-chart"
                        :height="230"
                        :labels="doughChart.labels"
                        :data-sets="doughChart.dataSet"
                    />
                    <div v-if="doughChart.labels.length > 0" class="chart-data-list">
                        <div class="row">
                            <div class="col-12" v-for="(item, index) in doughChart.dataList" :key="index">
                                <div class="data-group-item px-0">
                                    <span class="square" :style="`background-color: ${item.color}`"/>
                                    <span class="value">{{ item.name }} - {{ item.value }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!typeLoading" class="card-body p-0">
                <div class="datatable">
                    <div v-if="typeData && typeData.thead && typeData.thead.length > 0"
                         class="table-responsive custom-scrollbar table-view-responsive pt-3 mt-3">
                        <table class="table mb-0">
                            <thead>
                            <tr>
                                <th class="datatable-th pt-2">
                                    <span class="font-size-default"><span>{{ $t('date') }}</span></span>
                                </th>
                                <th class="datatable-th pt-2" v-for="name in typeData.thead">
                                    <span class="font-size-default"><span>{{ name }}</span></span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(type, date) in typeData.tbody">
                                <td class="datatable-td">{{ date === 'total' ? $t('total') : date }}</td>
                                <td v-for="item in type" class="datatable-td">{{ item }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else style="min-height: 400px;" class="position-relative"
                         :class="{'loading-opacity': typeLoading}">
                        <app-empty-data-block :message="$t('empty_data_block_dummy_message')"/>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="hasFinanceTab && tab === 'finance'" class="card border-0 bg-transparent">
            <div v-if="financeLoading" class="card-body">
                <app-overlay-loader v-if="financeLoading"/>
            </div>
            <div v-if="!financeLoading" class="pt-primary">
                <app-chart
                    v-if="financeChart.labels.length > 0"
                    type="bar-chart"
                    :height="380"
                    :maximum-range="20"
                    :labels="financeChart.labels"
                    :data-sets="financeChart.dataSet"
                />
            </div>
            <div v-if="!financeLoading" class="card-body p-0 pt-primary">
                <div class="datatable">
                    <div v-if="financeData && financeData.thead && financeData.thead.length > 0"
                         class="table-responsive custom-scrollbar table-view-responsive pt-3 mt-3">
                        <table class="table mb-0">
                            <thead>
                            <tr>
                                <th class="datatable-th pt-2" v-for="name in financeData.thead">
                                    <span class="font-size-default"><span>{{ name }}</span></span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(prices, date) in financeData.tbody">
                                <td class="datatable-td">{{ date === 'total' ? $t('total') : date }}</td>
                                <td v-for="(price, index) in prices" class="datatable-td">
                                    <span :style="{'color': index === 2 ? price > 0 ? 'green' : 'red' : ''}">
                                        {{ new Intl.NumberFormat('en-US').format(price) }}
                                    </span>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else style="min-height: 400px;" class="position-relative"
                         :class="{'loading-opacity': financeLoading}">
                        <app-empty-data-block :message="$t('empty_data_block_dummy_message')"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
/* Standard filter styling - matches other screens like HouseKeeping */
</style>

<script>
import HelperMixin from "../../../../../common/Mixin/Global/HelperMixin";
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {
    formatDateForServer,
    formatDateToLocal,
    lastMonth,
    lastWeek, lastYear,
    thisMonth,
    thisWeek, thisYear,
    today
} from "../../../../../common/Helper/Support/DateTimeHelper";
import {axiosGet} from "../../../../../common/Helper/AxiosHelper";
import DropDownFilter from "../../../../../core/components/filter/DropDownFilter";
import MultiSelectFilter from "../../../../../core/components/filter/MultiSelectFilter";
import DateRangePicker from "../../../../../core/components/date-range-picker/DateRangePicker";
import RoomHousekeepingDetailTab from "../Dashboard/RoomHousekeepingDetailTab";
import RoomHousekeepingSummaryTab from "../Dashboard/RoomHousekeepingSummaryTab";

export default {
    name      : "AppHotelShare",
    mixins    : [HelperMixin],
    components: {DropDownFilter, MultiSelectFilter, DateRangePicker, RoomHousekeepingDetailTab, RoomHousekeepingSummaryTab},
    props : {
        is_dashboard: false,
        hotel_id    : null,
        from_date   : today(),
        to_date     : today(),
        passcode    : null,
    },
    data() {
        return {
            TENANT_BASE_URL,
            typeLoading   : false,
            financeLoading: false,
            floorAssignmentLoading: false,
            filterStateVersion: 0,
            tab           : 'summary',
            range         : 'last_month',
            hotelName     : null,
            memberUrl     : null,
            hotelList     : [],
            staffList     : [],
            employeeTagList: [],
            rangeList     : [],
            typeData      : [],
            financeData   : [],
            floorAssignmentData : [],
            formData      : {
                from_date: formatDateForServer(this.from_date),
                to_date  : formatDateForServer(this.to_date),
                passcode : this.passcode || '',
                hotel_id : this.hotel_id,
                maid_id  : 0,
                employee_tags: '',
            },
            financeChart  : {
                labels : [],
                dataSet: [],
            },
            historySyncMode: 'replace',
            lineChart     : {
                labels : [],
                dataSet: [],
            },
            doughChart    : {
                labels  : [],
                dataList: [],
                dataSet : [
                    {
                        borderWidth    : 0,
                        backgroundColor: [],
                        data           : []
                    }
                ],
            },
            colors        : [
                '#FF5733',
                '#FF8D1A',
                '#FFC300',
                '#DAF7A6',
                '#33FF57',
                '#1AFF8D',
                '#33FFC3',
                '#1ADAF7',
                '#3398FF',
                '#5733FF',
                '#8D1AFF',
                '#C300FF',
                '#F7A6DA',
                '#FF33C3',
                '#FF1A8D',
                '#DA1A1A',
                '#F7811A',
                '#F4D03F',
                '#82E0AA',
                '#58D68D'
            ]
        }
    },
    watch: {
        'formData.hotel_id': {
            handler  : function (value) {
                const hotel_id = value && typeof value !== "undefined" ? value : 0
                this.memberUrl = `${TENANT_BASE_URL}selectable/hotel/member?field_id=employee_id&hotel_id=${hotel_id}`
            },
            immediate: true
        },
        range              : {
            handler  : function (value, oldValue) {
                if (value === 'today') {
                    this.formData.from_date = today().format('YYYY-MM-DD')
                    this.formData.to_date   = today().format('YYYY-MM-DD')
                }
                if (value === 'this_week') {
                    this.formData.from_date = thisWeek()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = thisWeek()[1].format('YYYY-MM-DD')
                }
                if (value === 'last_week') {
                    this.formData.from_date = lastWeek()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = lastWeek()[1].format('YYYY-MM-DD')
                }
                if (value === 'this_month') {
                    this.formData.from_date = thisMonth()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = thisMonth()[1].format('YYYY-MM-DD')
                }
                if (value === 'last_month') {
                    this.formData.from_date = lastMonth()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = lastMonth()[1].format('YYYY-MM-DD')
                }
                if (value === 'this_year') {
                    this.formData.from_date = thisYear()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = thisYear()[1].format('YYYY-MM-DD')
                }
                if (value === 'last_year') {
                    this.formData.from_date = lastYear()[0].format('YYYY-MM-DD')
                    this.formData.to_date   = lastYear()[1].format('YYYY-MM-DD')
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.initRangeList()
        if (this.is_dashboard) {
            this.hydrateDashboardStateFromUrl()
            window.addEventListener('popstate', this.handleBrowserNavigation);
        }
        this.loadHotelList()
        this.loadStaffList()
        this.loadEmployeeTagList()
        this.queryUrl()
    },
    beforeDestroy() {
        if (this.is_dashboard) {
            window.removeEventListener('popstate', this.handleBrowserNavigation);
        }
    },
    computed: {
        hiddenTabIds() {
            return ['finance'];
        },
        visibleTabs() {
            const tabs = [
                {id: 'cleaning', label: 'cleaning'},
                {id: 'finance', label: 'finance'},
                {id: 'summary', label: 'summary'},
                {id: 'detail', label: 'detail'},
            ];

            return tabs.filter(tab => !this.hiddenTabIds.includes(tab.id));
        },
        hasCleaningTab() {
            return !this.hiddenTabIds.includes('cleaning');
        },
        hasFinanceTab() {
            return !this.hiddenTabIds.includes('finance');
        },
        defaultTab() {
            return this.visibleTabs[0]?.id || 'detail';
        },
        dateRangePickerValue() {
            if (!this.formData.from_date || !this.formData.to_date) {
                return null;
            }

            const start = this.createInternalDate(this.formData.from_date);
            const end = this.createInternalDate(this.formData.to_date);

            if (!start || !end) {
                return null;
            }

            return {start, end};
        }
    },
    methods: {
        formatDateToLocal,
        normalizeTab(tab) {
            return this.visibleTabs.some(item => item.id === tab) ? tab : this.defaultTab;
        },
        resetDashboardData() {
            this.hotelName = null;
            this.typeData = [];
            this.financeData = [];
            this.floorAssignmentData = [];

            this.financeChart.labels = [];
            this.financeChart.dataSet = [];
            this.lineChart.labels = [];
            this.lineChart.dataSet = [];
            this.doughChart.labels = [];
            this.doughChart.dataSet[0].backgroundColor = [];
            this.doughChart.dataSet[0].data = [];
            this.doughChart.dataList = [];

            this.typeLoading = false;
            this.financeLoading = false;
            this.floorAssignmentLoading = false;
        },
        createInternalDate(value) {
            const date = new Date(value);

            if (isNaN(date.getTime())) {
                return null;
            }

            return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
        },
        hydrateDashboardStateFromUrl() {
            const query = new URLSearchParams(window.location.search);
            const hotelId = query.get('hotel_id');
            const maidId = query.get('maid_id');
            const employeeTags = query.get('employee_tags');
            const fromDate = query.get('from_date');
            const toDate = query.get('to_date');
            const tab = query.get('tab');

            this.formData.hotel_id = hotelId ? Number(hotelId) : (this.hotel_id ? Number(this.hotel_id) : null);
            this.formData.maid_id = maidId ? Number(maidId) : 0;
            this.formData.employee_tags = employeeTags || '';

            if (fromDate) {
                this.formData.from_date = fromDate;
                this.formData.to_date = toDate || fromDate;
            } else {
                this.formData.from_date = lastMonth()[0].format('YYYY-MM-DD');
                this.formData.to_date = lastMonth()[1].format('YYYY-MM-DD');
            }

            this.tab = this.normalizeTab(tab || this.defaultTab);
            this.filterStateVersion += 1;
        },
        syncDashboardUrl() {
            if (!this.is_dashboard) {
                return;
            }

            if (this.historySyncMode === 'none') {
                this.historySyncMode = 'replace';
                return;
            }

            const url = new URL(window.location.href);
            url.search = '';

            if (this.formData.hotel_id) {
                url.searchParams.set('hotel_id', this.formData.hotel_id);
            }

            if (this.formData.maid_id && Number(this.formData.maid_id) !== 0) {
                url.searchParams.set('maid_id', this.formData.maid_id);
            }

            if (this.formData.employee_tags) {
                url.searchParams.set('employee_tags', this.formData.employee_tags);
            }

            if (this.formData.from_date) {
                url.searchParams.set('from_date', this.formData.from_date);
            }

            if (this.formData.to_date) {
                url.searchParams.set('to_date', this.formData.to_date);
            }

            if (this.tab !== this.defaultTab) {
                url.searchParams.set('tab', this.tab);
            }

            if (this.historySyncMode === 'push') {
                window.history.pushState({}, '', url);
            } else {
                window.history.replaceState({}, '', url);
            }

            this.historySyncMode = 'replace';
        },
        handleBrowserNavigation() {
            this.historySyncMode = 'none';
            this.hydrateDashboardStateFromUrl();
            this.loadStaffList();
            this.queryUrl();
        },
        setTab(tab) {
            tab = this.normalizeTab(tab);

            if (this.tab === tab) {
                return;
            }

            this.historySyncMode = 'push';
            this.tab = tab;
            this.syncDashboardUrl();
        },
        queryUrl() {
            this.syncDashboardUrl();

            if (!this.formData.hotel_id) {
                this.resetDashboardData();
                return
            }

            this.typeLoading    = this.hasCleaningTab
            this.financeLoading = this.hasFinanceTab
            this.floorAssignmentLoading = true
            const date          = []
            const colors        = []
            const Errors        = (error) => {
                if (error.response && error.response.data) {
                    this.$toastr.e(error.response.data.message)
                } else {
                    this.$toastr.e(error.message)
                }
            }

            if (this.formData.from_date) {
                date.push(formatDateForServer(this.formData.from_date))
            }
            if (this.formData.to_date) {
                date.push(formatDateForServer(this.formData.to_date))
            }

            const query      = `date=${date.join(',')}&passcode=${this.formData.passcode}&maid_id=${this.formData.maid_id}`
            const hotelUrl   = `${TENANT_BASE_URL}api/hotel/${this.formData.hotel_id}/show?passcode=${this.formData.passcode}`
            const floorAssignmentUrl = `${TENANT_BASE_URL}api/hotel/floor-assignment/share?hotel_id=${this.formData.hotel_id}&${query}`

            this.financeChart.labels                   = []
            this.financeChart.dataSet                  = []
            this.lineChart.labels                      = []
            this.lineChart.dataSet                     = []
            this.doughChart.labels                     = []
            this.doughChart.dataSet[0].backgroundColor = []
            this.doughChart.dataSet[0].data            = []
            this.doughChart.dataList                   = []

            axiosGet(floorAssignmentUrl).then((res) => {
                this.floorAssignmentData = res.data
            }).catch(Errors).finally(() => {
                this.floorAssignmentLoading = false
            })

            if (this.hasCleaningTab || this.hasFinanceTab) {
                const typeUrl = `${TENANT_BASE_URL}api/hotel/type-report/share?hotel_id=${this.formData.hotel_id}&${query}`
                const financeUrl = `${TENANT_BASE_URL}api/hotel/house-keeping/finance?hotel_id=${this.formData.hotel_id}&${query}`

                if (this.hasFinanceTab) {
                    axiosGet(financeUrl).then((res) => {
                        this.financeData     = res.data
                        const labels         = Object.keys(res.data.tbody).filter(date => date !== 'total')
                        const contractPrices = labels.map(date => parseFloat(res.data.tbody[date][0]));
                        const sellingPrices  = labels.map(date => parseFloat(res.data.tbody[date][1]));
                        const profits        = labels.map(date => parseFloat(res.data.tbody[date][2]));

                        this.financeChart.labels  = labels
                        this.financeChart.dataSet = [
                            {
                                label          : this.$t('contract_price'),
                                data           : contractPrices,
                                backgroundColor: '#FF5733',
                                fill           : false,
                                tension        : 0.3
                            },
                            {
                                label          : this.$t('selling_price'),
                                data           : sellingPrices,
                                backgroundColor: '#088d49',
                                fill           : false,
                                tension        : 0.3
                            },
                            {
                                label          : this.$t('profit'),
                                data           : profits,
                                backgroundColor: '#DA1A1A',
                                fill           : false,
                                tension        : 0.3
                            }
                        ]
                    }).catch(Errors).finally(() => {
                        this.financeLoading = false
                    })
                }

                if (this.hasCleaningTab) {
                    axiosGet(typeUrl).then((res) => {
                        this.typeData = res.data
                        if (res.data.tbody) {
                            for (const date in res.data.tbody) {
                                if (date !== 'total') {
                                    this.lineChart.labels.push(date)
                                }
                            }
                            for (const index in res.data.thead) {
                                const color   = this.getColors(colors)
                                const dataSet = []

                                this.doughChart.dataSet[0].backgroundColor.push(color)
                                this.doughChart.dataSet[0].data.push(res.data.tbody.total[index] || 0)
                                this.doughChart.labels.push(res.data.thead[index])
                                this.doughChart.dataList.push({
                                    name : res.data.thead[index],
                                    value: res.data.tbody.total[index] || 0,
                                    color: color
                                })

                                for (const date in res.data.tbody) {
                                    if (date !== 'total') {
                                        dataSet.push(res.data.tbody[date][index] || 0)
                                    }
                                }

                                this.lineChart.dataSet.push({
                                    label          : res.data.thead[index],
                                    borderColor    : color,
                                    backgroundColor: 'transparent',
                                    data           : dataSet,
                                })

                                colors.push(color)
                            }
                        }
                    }).catch(Errors).finally(() => {
                        this.typeLoading = false
                    })
                }
            }

            axiosGet(hotelUrl).then((res) => {
                this.hotelName = res.data.name || null
            }).catch(Errors)
        },
        randColors() {
            const r = Math.floor(Math.random() * 255);
            const g = Math.floor(Math.random() * 255);
            const b = Math.floor(Math.random() * 255);

            return "rgb(" + r + "," + g + "," + b + ")";
        },
        getColors(saved) {
            const randKey = Math.floor(Math.random() * this.colors.length);
            const color   = this.colors[randKey];

            if (saved.includes(color)) {
                return this.getColors(saved)
            }

            return color
        },
        getAvatars(employees) {
            if (!employees || !employees.length) {
                return [];
            }
            return employees.map(item => {
                let imgUrl = undefined;
                if (item.profile_picture) {
                    imgUrl = item.profile_picture.full_url || item.profile_picture.file_url;
                }
                return {
                    img: imgUrl,
                    alt: item.full_name || '',
                    title: item.full_name || '',
                    subTitle: item.email
                };
            });
        },
        initRangeList() {
            this.rangeList = [
                {id: 'today', name: this.$t('today')},
                {id: 'this_week', name: this.$t('this_week')},
                {id: 'last_week', name: this.$t('last_week')},
                {id: 'this_month', name: this.$t('this_month')},
                {id: 'last_month', name: this.$t('last_month')},
                {id: 'this_year', name: this.$t('this_year')},
                {id: 'last_year', name: this.$t('last_year')},
            ]
        },
        loadHotelList() {
            axiosGet(`${TENANT_BASE_URL}selectable/hotel/list`)
                .then(({data}) => this.hotelList = data)
                .catch(error => this.$toastr.e(error.response?.data?.message || error.message))
        },
        loadStaffList() {
            const hotel_id = this.formData.hotel_id || 0
            const terminateFrom = this.formData.from_date || new Date().toISOString().slice(0, 10)
            axiosGet(`${TENANT_BASE_URL}selectable/hotel/member?field_id=employee_id&hotel_id=${hotel_id}&terminate_date_from=${terminateFrom}`)
                .then(({data}) => this.staffList = data)
                .catch(error => this.$toastr.e(error.response?.data?.message || error.message))
        },
        loadEmployeeTagList() {
            axiosGet(`${TENANT_BASE_URL}selectable/employee-tags`)
                .then(({data}) => this.employeeTagList = data)
                .catch(error => this.$toastr.e(error.response?.data?.message || error.message))
        },
        onHotelChange(event) {
            this.historySyncMode = 'push';
            this.formData.hotel_id = event.value || null
            this.formData.maid_id = 0
            this.staffList = []
            this.loadStaffList()
            this.queryUrl()
        },
        onStaffChange(event) {
            this.historySyncMode = 'push';
            this.formData.maid_id = event.value || 0
            this.queryUrl()
        },
        onEmployeeTagsChange(event) {
            this.historySyncMode = 'push';
            const value = event && Object.prototype.hasOwnProperty.call(event, 'value') ? event.value : event;
            this.formData.employee_tags = value || '';
            this.queryUrl()
        },
        onRangeChange(value) {
            this.range = value
            // range watcher will update dates; trigger query so filters apply immediately
            this.queryUrl()
        },
        onDateRangeChange(value) {
            this.historySyncMode = 'push';

            if (value && value.start) {
                this.formData.from_date = value.start
                this.formData.to_date = value.end || value.start
            } else {
                this.formData.from_date = null
                this.formData.to_date = null
            }
            // auto query when date range changed
            this.queryUrl()
        }
    }
}
</script>
